import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { ToonPanelView } from '@/types/coaching'

interface Props {
  stage: 'intro' | 'outro'
  panels: ToonPanelView[]
}

export function ToonPanelList({ stage, panels }: Props) {
  const { t } = useTranslation('coaching')
  const [activeIndex, setActiveIndex] = useState(0)
  const panel = panels[activeIndex]

  if (!panel) return null

  return (
    <section className="space-y-2" aria-label={t(`toon.stage.${stage}`)}>
      {panels.length > 1 && (
        <div className="flex w-full items-center justify-center gap-1 overflow-x-auto" aria-label={t(`toon.stage.${stage}`)}>
          {panels.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveIndex(idx)}
              className={clsx(
                'flex h-6 min-w-6 items-center justify-center rounded-full border px-2 text-[11px] font-semibold shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
                idx === activeIndex
                  ? 'border-sky-500 bg-sky-50 text-sky-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50',
              )}
              aria-current={idx === activeIndex ? 'true' : undefined}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      )}
      <article className="mx-auto w-full max-w-[420px] rounded-md border border-gray-200 bg-white p-4 text-xs text-gray-800 shadow-sm">
        <div className="flex min-h-[180px] flex-col justify-center gap-3">
          <div className="flex flex-col gap-3">
            {panel.text && (
              <p className="whitespace-pre-wrap text-center text-sm leading-6 text-gray-900">
                {panel.text}
              </p>
            )}
            {panel.audioUrl && (
              <audio
                controls
                src={panel.audioUrl}
                data-testid={`panel-audio-${activeIndex}`}
                className="h-8 w-full"
              />
            )}
          </div>
          {panel.imageUrl && (
            <div className="flex justify-center">
              <img
                src={panel.imageUrl}
                alt={`panel-${activeIndex}`}
                className="max-h-80 w-auto max-w-full rounded-md object-contain"
              />
            </div>
          )}
        </div>
        {stage === 'outro' && panel.correctAnswer != null && (
          <div className="mt-3 grid gap-2 border-t border-gray-100 pt-3 sm:grid-cols-2">
            {[panel.answer0, panel.answer1]
              .map((answer, answerIdx) => ({ answer, key: answerIdx === 0 ? 'answer_0' : 'answer_1' }))
              .filter((item): item is { answer: string; key: 'answer_0' | 'answer_1' } => item.answer != null)
              .map(({ answer, key }) => {
                const isCorrect = panel.correctAnswer === key || panel.correctAnswer === answer
                return (
                  <span
                    key={answer}
                    data-testid={`panel-answer-${answer}-${isCorrect ? 'correct' : 'incorrect'}`}
                    className={clsx(
                      'flex min-h-9 items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs',
                      isCorrect
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-rose-200 bg-rose-50 text-rose-800',
                    )}
                  >
                    <span className="font-medium">{answer}</span>
                    <span className="text-[11px] font-semibold">
                      {isCorrect ? t('quiz.correct') : t('quiz.incorrect')}
                    </span>
                  </span>
                )
              })}
          </div>
        )}
      </article>
    </section>
  )
}
